import { Component } from '@angular/core';
import { IonicPage, ModalController, NavController, NavParams, ToastController } from 'ionic-angular';
import {
  DailyLogOperationInstance,
  DailyOperationLogRecordHelper,
  StepInstance,
  TransitingHelper,
  VoyageLogCreateSelectOptions,
  VoyageLogEndSelectOptions
} from '../../../models/projects/operation.models';
import {
  LocationOption,
  LocationSelectOptions,
  SetupChangeLogRecord,
  SetupMetadata,
  UpdatedSetupSummary
} from '../../../models/setup/setup.models';
import { SetupService } from '../../../services/setup/setup.service';
import { DateService } from '../../../services/date/date.service';
import { NavMainMenuPage } from '../../nav/nav-menus/nav-main-menu/nav-main-menu';
import { DailyLogPage } from '../../daily-log/daily-log/daily-log';
import { OperationService } from '../../../services/operations/operation/operation.service';
import { NavHelperService } from '../../../services/nav/nav-helper.service';
import { OperationSaveService } from '../../../services/operations/operation-save/operation-save.service';
import { DailyLogService } from '../../../services/daily-log/daily-log.service';
import { TransitingSaveService } from '../../../services/operations/transiting-save/transiting-save.service';
import { DailyLogModalPage } from '../../daily-log/daily-log-modal/daily-log-modal';
import { AppData, OperationInstanceSaveRequest } from '../../../models/app-data/app-data';
import { AppDataSyncService } from '../../../services/app-data-sync/app-data-sync.service';
import { JackingActivityService } from '../../../services/jacking-activity/jacking-activity.service';
import { VesselService } from '../../../services/vessel/vessel.service';
import { OnSiteMovementService } from '../../../services/on-site-movement/on-site-movement.service';
import { ApiOption } from '../../../_foundation/models/base.models';
import { VoyageLogService } from '../../../services/voyage-log/voyage-log.service';
import { VoyageLogSummary } from '../../../models/voyage-log/voyagelog.models';
import { OperationTimeService } from '../../../services/operations/operation-time/operation-time.service';
import { JackingActivityEditor } from 'models/projects/jacking-activity.models';
import { PtLuxonDate } from '../../../models/projects/date.models';
import { AffectedTransitHelper, TransitSummary } from '../../../models/transit/transit.models';
import { TransitService } from '../../../services/transit/transit.service';
import { DateParserService } from '../../../services/daily-log/daily-log-time/date-parser-service';
import { AppConfig } from '../../../app/app.config';
import { cloneDeep } from 'lodash';
import { DateTime } from 'luxon';
import { ErrorType, ValidationResult } from '../../../models/projects/validation.models';

@IonicPage()
@Component({
  selector: 'page-operation-details-location-select',
  templateUrl: 'operation-details-location-select.html',
})
export class OperationDetailsLocationSelectPage {
  config = AppConfig.settings;

  loading: boolean = true;
  voyageLoading: boolean;
  saved: boolean = false;
  dailyLogDate: PtLuxonDate;
  instance: DailyLogOperationInstance;
  stepInstances: StepInstance[] = [];
  setupSummary: UpdatedSetupSummary;
  setupMetadata: SetupMetadata;
  currentLocation: string;
  locationTimeChanged: string;
  locationOptions: LocationOption[] = [];
  selectedLocation: LocationOption;
  locationSelectOptions: any = new LocationSelectOptions().options;
  newLocationId: number;

  voyageLogForOperationTime: VoyageLogSummary;
  precedingVoyageLog: VoyageLogSummary;
  voyageLogValidationResult: ValidationResult = new ValidationResult();
  voyageLogOptions: ApiOption<number>[] = [];
  voyageLogCreateSelectOptions: any = new VoyageLogCreateSelectOptions().options;
  voyageLogEndSelectOptions: any = new VoyageLogEndSelectOptions().options;
  voyageLogCreateId: number;
  showVoyageCreationOption: boolean;
  showVoyageEndOption: boolean;
  voyageLogEndId: number;
  voyageDistance: number;
  jackingDataUpdated: boolean;

  affectedTransitHelper: AffectedTransitHelper;
  transitPreview: TransitSummary;

  constructor(public navCtrl: NavController,
              public navParams: NavParams,
              public setupService: SetupService,
              public dateService: DateService,
              public operationService: OperationService,
              public operationSaveService: OperationSaveService,
              public dailyLogService: DailyLogService,
              public operationTimeService: OperationTimeService,
              public transitingSaveService: TransitingSaveService,
              private appDataSyncService: AppDataSyncService,
              public navHelperService: NavHelperService,
              public modalController: ModalController,
              private toastCtrl: ToastController,
              private jackingActivitySerivce: JackingActivityService,
              private vesselService: VesselService,
              private onSiteMovementService: OnSiteMovementService,
              private voyageLogService: VoyageLogService,
              private transitService: TransitService,
              private dateParserService: DateParserService,
  ) {
    this.instance = navParams.get('instance');
    this.stepInstances = navParams.get('stepInstances');
    this.dailyLogDate = navParams.get('dailyLogDate');
  }

  openDailyLogModal() {
    const dailyLogModal = this.modalController.create(DailyLogModalPage);
    dailyLogModal.present();
  }


  async ionViewDidLoad() {
    this.jackingDataUpdated = false;
    this.appDataSyncService.refreshJackingDataForDate(this.dailyLogDate).then(() => {
      this.jackingDataUpdated = true;
    });
    const vesselEditor = await this.vesselService.getEditor();
    this.affectedTransitHelper = await this.transitService.getAffectedTransits(this.instance.startTime, this.instance.endTime);

    this.setupService
      .getSetupMetadata(vesselEditor.id, this.dailyLogDate.dateTime)
      .then(async metadata => {
        this.setupMetadata = metadata;

        await this.setupService
          .getSetupSummary(this.instance.endTime.dateTime == null ? this.instance.startTime : this.instance.endTime, false)
          .then(async setupSummary => {
            this.setupSummary = !setupSummary ? new UpdatedSetupSummary() : setupSummary;
            this.locationTimeChanged = await this.dateService
              .setFullTimeString(this.setupSummary.transitingTimeChanged && this.setupSummary.transitingTimeChanged.dateTime
                ? this.setupSummary.transitingTimeChanged.dateTime
                : this.setupSummary.locationTimeChanged
                  ? this.setupSummary.locationTimeChanged.dateTime
                  : null);
            this.newLocationId = this.setupSummary.transitingLocationId ? this.setupSummary.transitingLocationId : this.setupSummary.locationId;

            const projectOptions = this.setupMetadata.projectOptions;
            const project = projectOptions.find(e => e.id === this.setupSummary.projectId);
            const phaseOptions = project ? project.phaseOptions : projectOptions[0].phaseOptions;
            const phase = phaseOptions.find(e => e.id === this.setupSummary.phaseId);
            const stageOptions = phase ? phase.stageOptions : phaseOptions[0].stageOptions;
            const stage = stageOptions.find(e => e.id === this.setupSummary.stageId);
            this.locationOptions = stage ? stage.locationOptions : stageOptions[0].locationOptions;
            this.selectedLocation = this.locationOptions.find(e => e.id == this.newLocationId);

            this.transitPreview = await this.transitService
              .createOrUpdateTransitSummary(
                this.affectedTransitHelper,
                this.instance,
                !this.affectedTransitHelper.transitAfterOperation && this.newLocationId !== (this.setupSummary.transitingLocationId
                ? this.setupSummary.transitingLocationId
                : this.setupSummary.locationId));

            this.loading = false;
          });
      });

    if (this.instance.projectSpecificOperation.operationSummary.subClassificationName === 'Harbour Manoeuvres') {
      this.voyageLoading = true;
      const operationTime = this.instance.startTime.dateTime;
      const voyageData = await Promise.all([
        this.voyageLogService.getVoyageLogForTime(operationTime),
        this.voyageLogService.getLatestVoyageLogBeforeTime(operationTime)
      ]);

      this.voyageLogForOperationTime = voyageData[0];
      this.precedingVoyageLog = voyageData[1];

      if (!this.voyageLogForOperationTime) {
        this.showVoyageCreationOption = true;
        this.voyageLogOptions = [{id: 1, name: 'Yes'}, {id: 2, name: 'No'}];
      } else {
        this.showVoyageEndOption = true;
        this.voyageLogOptions = [{id: 1, name: 'Yes'}, {id: 2, name: 'No'}];
      }

      this.voyageLoading = false;
    }
  }

  async updateTransitSummary(locationId: number) {
    this.selectedLocation = this.locationOptions.find(e => e.id == locationId);
    this.newLocationId = locationId;

    this.affectedTransitHelper = await this.transitService.getAffectedTransits(this.instance.startTime, this.instance.endTime);

    this.transitPreview = await this.transitService
      .createOrUpdateTransitSummary(
        this.affectedTransitHelper,
        this.instance,
        !this.affectedTransitHelper.transitAfterOperation && this.newLocationId !== (this.setupSummary.transitingLocationId
        ? this.setupSummary.transitingLocationId
        : this.setupSummary.locationId));
  }

  async save() {
    this.loading = true;

    if (!!this.saved) {
      return;
    }

    // ensures that the local offset has no bearing on what actually gets saved in the database
    this.instance.newLocationId = this.newLocationId;
    // if location is the same - don't set previousId - we use it only in helping decide what to display in the daily log setup summary
    this.instance.previousLocationId = this.setupSummary.transitingLocationId
      ? this.setupSummary.locationId
      : this.newLocationId !== this.setupSummary.locationId
        ? this.setupSummary.locationId
        : null;

    let saveRequest: OperationInstanceSaveRequest = {
      operationInstanceEditor: cloneDeep(this.instance) as DailyLogOperationInstance,
      dailyLogDate: cloneDeep(this.dailyLogDate.dateTime) as DateTime,
      newLocationId: this.newLocationId
    };

    saveRequest = await this.handleJackingActivityCreationCheck(saveRequest);
    saveRequest = await this.handleVoyageLogChoice(saveRequest);

    if (!saveRequest) {
      this.loading = false;
      return;
    }

    const previousLocationInTransit = await this.getPreviousLocationInTransit();
    await this.transitService
      .updateOrCreateTransitForSave(saveRequest, this.affectedTransitHelper, saveRequest.operationInstanceEditor, previousLocationInTransit == null);

    await this.operationSaveService.saveOperationInstance(saveRequest)
      .then(async savedInstance => {
        this.navHelperService.unsavedChanges = false;
        this.saved = true;
        this.navHelperService.warned = true;

        this.presentSaveToast().then(() => {
          this.navCtrl.setRoot(DailyLogPage);
          this.navHelperService.updateNavMenuPage({page: NavMainMenuPage});
        });
      }, async error => {
        this.loading = false;
        throw (error);
      });
  }

  async handleJackingActivityCreationCheck(saveRequest: OperationInstanceSaveRequest) {
    if (!this.jackingDataUpdated) {
      await this.appDataSyncService.refreshJackingDataForDate(this.dailyLogDate).then(() => {
        this.jackingDataUpdated = true;
      });
    }

    if (this.jackingActivitySerivce.operationRequiresJackingActivity(saveRequest.operationInstanceEditor)) {
      if (this.dailyLogDate.dateTime.toUTC().toMillis() >= this.config.jackingV2StartDateUtc.toMillis()) {
        this.handleJackingV2Creation(saveRequest);
      } else {
        await this.handleDeprecatedJackingCreation(saveRequest);
      }
    }

    if (this.onSiteMovementService.operationRequiresOnSiteMovementRecord(saveRequest.operationInstanceEditor)) {
      const onSiteMovementRecord = await this.onSiteMovementService.createOnSiteMovementRecordForOperation(saveRequest.operationInstanceEditor, this.newLocationId);
      if (onSiteMovementRecord) {
        saveRequest.onSiteMovementEditor = onSiteMovementRecord;
      }
    }

    return saveRequest;
  }

  async handleVoyageLogChoice(saveRequest: OperationInstanceSaveRequest) {
    this.voyageLogValidationResult = new ValidationResult();

    if (this.voyageLogCreateId === 1) {
      const creationValidation = await this.voyageLogService.validateVoyageCreation(
        this.instance.startTime.dateTime);

      if (!creationValidation.canCreate) {
        this.voyageLogValidationResult.addError(
          creationValidation.reason,
          ErrorType.Default
        );
        return null;
      }

      // need to get the latest location instead of using 'this.setupSummary.locationId' - as if the current operation is already
      // mid-transit - then the setup summary will be the transiting to record not the actual location record
      const latestLocation = await this.setupService.getSetupLocationForTime(
        saveRequest.operationInstanceEditor.startTime,
        this.setupSummary.transitingLocationId);
      saveRequest.voyageLog = this.voyageLogService.createVoyageLogSummary(
        saveRequest.operationInstanceEditor,
        latestLocation.newValueId,
        this.precedingVoyageLog);
      saveRequest.voyageLog.voyageNumber = creationValidation.proposedVoyageNumber;
    } else if (this.voyageLogEndId === 1) {
      if (!this.voyageLogForOperationTime) {
        this.voyageLogValidationResult.addError(
          'No voyage is active at this operation time, so this operation cannot end a voyage.',
          ErrorType.Default
        );
        return null;
      }

      saveRequest.voyageLog = this.voyageLogForOperationTime;
      saveRequest.voyageLog.distance = Number(this.voyageDistance);

      if (saveRequest.operationInstanceEditor.endTime) {
        saveRequest.voyageLog.endDate = saveRequest.operationInstanceEditor.endTime.dateTime;
      }

      saveRequest.voyageLog.endLocationId = this.setupSummary.transitingLocationId ? this.setupSummary.transitingLocationId : this.setupSummary.locationId;
      saveRequest.voyageLog.endOperationId = saveRequest.operationInstanceEditor.id
        ? saveRequest.operationInstanceEditor.id
        : saveRequest.operationInstanceEditor.localId;
    }

    return saveRequest;
  }

  // gets the previous location change record, if in the same 'transit' that the user is currently making
  async getPreviousLocationInTransit(): Promise<SetupChangeLogRecord> {
    const previousLocation = await this.setupService.getLocationForTime(this.instance.endTime && this.instance.endTime.dateTime
      ? this.instance.endTime.dateTime
      : this.instance.startTime.dateTime
      , true);
    if (previousLocation == null || this.newLocationId !== previousLocation.newValueId) {
      return null;
    }

    return previousLocation;
  }

  presentSaveToast(): Promise<any> {
    const toast = this.toastCtrl.create({
      message:
        `${this.instance.isConcurrentOperation ? 'Concurrent ' : 'Contractual '}`
        + 'Operation \''
        + `${this.instance.projectSpecificOperation.name
        ? this.instance.projectSpecificOperation.name
        : this.instance.projectSpecificOperation.operationSummary.name}`
        + '\' added to the Daily Log',
      duration: 6000,
      position: 'bottom'
    });

    return toast.present();
  }

  private handleJackingV2Creation(saveRequest: OperationInstanceSaveRequest) {
    saveRequest.operationInstanceEditor.createJackingFormInstance = true;
    saveRequest.operationInstanceEditor.isArrivalOperation = this.jackingActivitySerivce.isArrivalJackingOperation(this.instance);
  }

  private async handleDeprecatedJackingCreation(saveRequest: OperationInstanceSaveRequest) {
    let jackingActivity: JackingActivityEditor;

    // when adding a new op into an existing transit, this getLocationForTime will return the current location change (that is attached
    // to the previous op in the transits' end time) so we use the previousValueId in the createOrUpdateActivityForOperation
    const setupRecordAtStart = await this.setupService.getLocationForTime(cloneDeep(saveRequest.operationInstanceEditor.startTime.dateTime), false);
    // if setupAtStart.newValue !== the selected newLocationId, then we are now changing location, so previous location id
    // will be the setupAtStart newValue. if they are the same, then we aren't transiting so it will be previousValueId
    const lastLocationId = setupRecordAtStart.newValueId !== this.newLocationId
      ? setupRecordAtStart.newValueId
      : setupRecordAtStart.previousValueId;
    jackingActivity = await this.jackingActivitySerivce.createOrUpdateActivityForOperation(
      saveRequest.operationInstanceEditor, lastLocationId, setupRecordAtStart.newValueId, this.newLocationId);
    if (jackingActivity) {
      saveRequest.jackingActivityEditor = cloneDeep(jackingActivity);
    }
  }
}



// WEBPACK FOOTER //
// ./src/pages/operations/operation-details-location-select/operation-details-location-select.ts